import { forwardRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { SegmentContextProvider } from "./context";
import useControllableState from "../../../utils/useControllableState";

const Segment = forwardRef((props, ref) => {
  const {
    value: valueProp,
    defaultValue,
    onChange = () => {},
    children,
    className,
    selectionType,
    ...rest
  } = props;

  const [value, setValue] = useControllableState({
    prop: valueProp,
    defaultProp: defaultValue,
    onChange: onChange,
    selectionType,
  });

  const onActive = useCallback(
    (itemValue) => {
      setValue(itemValue);
    },
    [setValue]
  );

  const onDeactivate = useCallback(
    (itemValue) => {
      if (selectionType === "single") {
        setValue([]);
      }

      if (selectionType === "multiple") {
        setValue((prevValue = []) => {
          return prevValue.filter((value) => value !== itemValue);
        });
      }

      if (selectionType === "quantity") {
        setValue((prevValue = []) => {
          return prevValue.filter((entry) => entry && entry.id !== itemValue);
        });
      }
    },
    [setValue, selectionType]
  );

  const segmentValue = useMemo(() => {
    if (selectionType === "single") {
      if (value && typeof value === "string") {
        return [value];
      }

      if (value && Array.isArray(value)) {
        return value;
      }

      return [];
    }

    if (selectionType === "multiple") {
      return value ? value : [];
    }

    // quantity: array of {id, qty} entries (unit counts).
    if (selectionType === "quantity") {
      return Array.isArray(value) ? value : [];
    }
  }, [selectionType, value]);

  return (
    <SegmentContextProvider
      value={{
        value: segmentValue,
        onActive: onActive,
        onDeactivate: onDeactivate,
        selectionType,
      }}
    >
      <div ref={ref} className={classNames("segment", className)} {...rest}>
        {children}
      </div>
    </SegmentContextProvider>
  );
});

Segment.defaultProps = {
  selectionType: "single",
};

Segment.propTypes = {
  selectionType: PropTypes.oneOf(["single", "multiple", "quantity"]),
  // string[] in single/multiple mode, {id, qty}[] in quantity mode.
  value: PropTypes.array,
  defaultValue: PropTypes.array,
};

export default Segment;
